import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatedHero } from '../components/ui/animated-hero';
import Button from '../components/Button';
import { ArrowRight, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useMouseTrail } from '../hooks/useMouseTrail';
import { isLovablePreview, prefersReducedMotion } from '../utils/env';

// Content
import companyData from '../content/company.json';
import servicesData from '../content/services.json';
import chrisProjectsData from '../content/chris-projects.json';

// Images
import { getChrisPortraitUrl } from '../config/images';
import RelatedContent from '../components/RelatedContent';

const HomePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentLang = location.pathname.startsWith('/en') ? 'en' : 'de';

  // Delayed mouse trail initialization to prevent preview issues
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const isPreview = isLovablePreview();
    const reducedMotion = prefersReducedMotion();

    if (isPreview) {
      console.info('[MouseTrail] Disabled: Lovable preview detected');
      return;
    }

    if (reducedMotion) {
      console.info('[MouseTrail] Disabled: User prefers reduced motion');
      return;
    }

    // Delay initialization until window is fully loaded
    const start = () => {
      console.info('[MouseTrail] Enabled: Initializing effect');
    };

    if (document.readyState === 'complete') {
      requestAnimationFrame(start);
    } else {
      window.addEventListener('load', () => requestAnimationFrame(start), { once: true });
    }
  }, []);

  // Disable mouse trail for a cleaner, professional hero
  useMouseTrail({
    colors: ['#FFFFFF'],
    size: 0,
    opacity: 0,
    trailLength: 0,
    enabled: false
  });

  const company = companyData[currentLang as keyof typeof companyData];
  const services = servicesData[currentLang as keyof typeof servicesData];

  // Get real projects from ChrisProjectsSection data
  const projects = chrisProjectsData[currentLang as keyof typeof chrisProjectsData];

  // Map real projects to portfolio format - using actual project data
  const portfolioItems = [
    {
      image: '/assets/Example Images/style art/art-investing-what-echoes-1.webp',
      title: projects.mainVentures[0]?.name || 'Competa AG',
      status: projects.mainVentures[0]?.description || (currentLang === 'de' ? 'Arbeitsvermittlung & Personalverleih' : 'Job Placement & Personnel Leasing'),
      result: currentLang === 'de' ? 'Business • Workforce' : 'Business • Workforce',
      url: projects.mainVentures[0]?.url
    },
    {
      image: '/assets/Example Images/style art/art-capital-rhythm-1.webp',
      title: currentLang === 'de' ? 'Legacy of Joy' : 'Legacy of Joy',
      status: currentLang === 'de' ? 'Der Weg zur finanziellen Freiheit' : 'The path to financial freedom',
      result: currentLang === 'de' ? 'Vermögensstrukturierung' : 'Wealth Structuring',
      url: '#'
    },
    {
      image: '/assets/Example Images/style object/object-bridge-finance-music-1.webp',
      title: projects.plaschyGroup.find(p => p.id === 'social-music-network')?.name || 'Social Music Network',
      status: projects.plaschyGroup.find(p => p.id === 'social-music-network')?.description || (currentLang === 'de' ? 'Verbinde dich durch Musik' : 'Connect through music'),
      result: currentLang === 'de' ? 'Musik & Community' : 'Music & Community',
      url: projects.plaschyGroup.find(p => p.id === 'social-music-network')?.url
    },
    {
      image: '/assets/Example Images/style art/art-capital-rhythm-2.webp',
      title: projects.plaschyGroup.find(p => p.id === 'yschy')?.name || 'Y.schy.CH',
      status: projects.plaschyGroup.find(p => p.id === 'yschy')?.description || (currentLang === 'de' ? 'The #YschyCH Movement' : 'The #YschyCH Movement'),
      result: currentLang === 'de' ? 'Bewegung & Community' : 'Movement & Community',
      url: projects.plaschyGroup.find(p => p.id === 'yschy')?.url
    },
  ];

  // Create a single carousel set without duplicates
  const carouselItems = [...portfolioItems];

  return (
    <>
      <Helmet>
        <title>{currentLang === 'de' ? 'Vermögen gestalten mit Freude & Bestand | Chris Richard Plaschy Treuhand' : 'Build Wealth That Brings Joy and Lasts | Chris Richard Plaschy Treuhand'}</title>
        <meta name="description" content={currentLang === 'de' ? 'Zertifizierter Treuhänder (eidg. FA) mit OCPD-Expertise für Legacy-Planung und systematische Vermögensstrukturierung für vermögende Familien in Zug.' : 'Certified fiduciary (Swiss Federal Diploma) with OCPD expertise for legacy planning and systematic wealth structuring for affluent families.'} />
        <meta name="keywords" content="Legacy-Planung, Vermögensstrukturierung, Treuhänder, Zug, Schweiz, OCPD, eidg. Fachausweis" />
        <meta property="og:title" content={`${company.name} - ${company.tagline}`} />
        <meta property="og:description" content={company.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://treuhand-beratungen.ch${location.pathname}`} />
        <meta property="og:image" content={getChrisPortraitUrl()} />
      </Helmet>

      {/* Hero Section with Animated Rotating Words */}
      <AnimatedHero
        title={
          currentLang === 'de' ? (
            <>Gewinnen Sie die Freude an Ihrem <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-slate-900">Erfolg</span></>
          ) : (
            <>Reclaim the Joy of Your <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-slate-900">Success</span>.</>
          )
        }
        subtitle={currentLang === 'de'
          ? "Finanzielle Komplexität sollte keine Last sein. Wir verwandeln schwierige Herausforderungen in perfekte Systeme und geben Ihnen die absolute Freiheit, sich auf das zu konzentrieren, was für Ihre Familie und Ihre Zukunft wirklich zählt."
          : "Financial complexity shouldn't be a burden. We transform intricate challenges into flawless systems, giving you the absolute freedom to focus on what truly matters to your family and future."}
        description={currentLang === 'de' ? "Zertifizierter Treuhänder (eidg. FA) mit OCPD-Expertise" : "Certified fiduciary (Swiss Federal Diploma) with OCPD expertise"}
        primaryCta={{
          text: currentLang === 'de' ? "30 Min. Erstgespräch — kostenlos" : "30 Min. Consultation — Free",
          href: `/${currentLang}/contact`
        }}
        secondaryCta={{
          text: currentLang === 'de' ? "Methodik verstehen" : "Understand Methodology",
          href: `/${currentLang}/about`
        }}
        images={[
          "/assets/ChristianPlushy/chris-conductor-black-suit-outdoor.webp"
        ]}
      />

      {/* Trusted Partner Section - Rebuilt as requested */}
      <section className="py-12 md:py-24 bg-white border-b border-gray-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="mb-10 md:mb-24 text-center max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight mb-6 md:mb-8 leading-[1.1]">
                {currentLang === 'de'
                  ? 'Ihr vertrauenswürdiger Partner für systematischen Erfolg'
                  : 'Your trusted partner in unlocking business potential'}
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-24 items-start">
              {/* Text Content */}
              <div className="space-y-6 md:space-y-8 lg:sticky lg:top-32">
                <p className="text-base md:text-xl text-slate-600 leading-relaxed">
                  {currentLang === 'de'
                    ? 'Wir verstehen, dass jedes Unternehmen einzigartig ist und es keine Einheitslösung gibt. Deshalb verfolgen wir einen personalisierten Ansatz, um maßgeschneiderte Strategien zu liefern, die auf Ihre Ziele, Herausforderungen und Branchendynamik abgestimmt sind.'
                    : 'At Plaschy Treuhand, we understand that every business is unique, and there is no one-size-fits-all solution. That\'s why we take a personalized approach to deliver tailored strategies that align with your goals, challenges, and industry dynamics.'}
                </p>

                <div className="space-y-4 md:space-y-6 pt-4">
                  {[
                    currentLang === 'de' ? 'Strategische Expertise' : 'Strategic Expertise',
                    currentLang === 'de' ? 'Maßgeschneiderte Lösungen' : 'Customized Solutions',
                    currentLang === 'de' ? 'Ganzheitlicher Ansatz' : 'Full-Service Solutions'
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center group">
                      <div className="w-6 h-6 mr-4 bg-amber-100 flex items-center justify-center text-amber-600 group-hover:bg-amber-200 transition-colors">
                        <CheckCircle2 className="w-4 h-4" />
                      </div>
                      <span className="text-base md:text-lg font-medium text-slate-900">{item}</span>
                    </div>
                  ))}
                </div>

                <div className="pt-6 md:pt-8">
                  <Button
                    variant="outline"
                    className="w-16 h-16 p-0 flex items-center justify-center border-2 border-accent-copper bg-accent-copper text-slate-900 hover:bg-accent-copper-light hover:border-accent-copper-light hover:scale-110 transition-all duration-300 shadow-xl hover:shadow-2xl font-bold"
                    onClick={() => navigate(`/${currentLang}/about`)}
                    aria-label={currentLang === 'de' ? "Mehr erfahren" : "Learn more"}
                  >
                    <ArrowRight className="w-6 h-6" />
                  </Button>
                </div>
              </div>

              {/* Image */}
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src="/assets/ChristianPlushy/chris-black-suit-golden-tie-professional.webp"
                  alt="Strategic Business Planning"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - New Layout */}
      <section className="py-12 md:py-20 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Row */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-10 md:mb-16 gap-6 md:gap-8">
            <div className="max-w-3xl">
              <h2 className="text-3xl md:text-5xl font-bold text-slate-900 leading-tight">
                {currentLang === 'de' ? 'Wie wir Ihrem Unternehmen helfen können' : 'How we can help your business'}
              </h2>
            </div>
            <Button
              variant="primary"
              size="lg"
              className="w-full md:w-auto font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
              onClick={() => navigate(`/${currentLang}/services`)}
            >
              {currentLang === 'de' ? 'Alle Services ansehen' : 'View All Services'}
            </Button>
          </div>

          {/* Content Row */}
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            {/* Left Image Column */}
            <div className="lg:w-1/3 relative h-[400px] lg:h-auto overflow-hidden group">
              <img
                src="/assets/ChristianPlushy/chris-cyan-suit-profile-portrait.webp"
                alt="Service Overview"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>

            {/* Right Grid Column */}
            <div className="lg:w-2/3 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-12 content-start">
              {/* Service 01 - BUILD */}
              <div className="group bg-white p-8 shadow-sm border-2 border-slate-200 hover:bg-amber-50 hover:border-accent-copper hover:shadow-xl transition-all duration-300 cursor-pointer">
                <span className="text-slate-400 text-lg font-medium mb-4 block">01</span>
                <div className="h-px w-full bg-slate-200 mb-6 group-hover:bg-accent-copper group-hover:h-0.5 transition-all duration-300"></div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">
                  {services.build.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {services.build.shortDescription}
                </p>
              </div>

              {/* Service 02 - SCALE */}
              <div className="group bg-white p-8 shadow-sm border-2 border-slate-200 hover:bg-amber-50 hover:border-accent-copper hover:shadow-xl transition-all duration-300 cursor-pointer">
                <span className="text-slate-400 text-lg font-medium mb-4 block">02</span>
                <div className="h-px w-full bg-slate-200 mb-6 group-hover:bg-accent-copper group-hover:h-0.5 transition-all duration-300"></div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">
                  {services.scale.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {services.scale.shortDescription}
                </p>
              </div>

              {/* Service 03 - INVEST */}
              <div className="group bg-white p-8 shadow-sm border-2 border-slate-200 hover:bg-amber-50 hover:border-accent-copper hover:shadow-xl transition-all duration-300 cursor-pointer">
                <span className="text-slate-400 text-lg font-medium mb-4 block">03</span>
                <div className="h-px w-full bg-slate-200 mb-6 group-hover:bg-accent-copper group-hover:h-0.5 transition-all duration-300"></div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">
                  {services.invest.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {services.invest.shortDescription}
                </p>
              </div>

              {/* Service 04 - Custom Solutions (differentiated CTA card) */}
              <div className="group bg-slate-900 p-8 shadow-sm border-2 border-slate-800 hover:border-accent-copper hover:shadow-xl transition-all duration-300 cursor-pointer">
                <span className="text-slate-500 text-lg font-medium mb-4 block">04</span>
                <div className="h-px w-full bg-slate-700 mb-6 group-hover:bg-accent-copper group-hover:h-0.5 transition-all duration-300"></div>
                <h3 className="text-2xl font-bold text-white mb-3">
                  {currentLang === 'de' ? 'Individuelle Lösungen' : 'Custom Solutions'}
                </h3>
                <p className="text-slate-400 leading-relaxed">
                  {currentLang === 'de'
                    ? 'Maßgeschneiderte Strategien für Ihre spezifischen Herausforderungen.'
                    : 'Tailored strategies designed for your specific business challenges.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio / Recent Projects */}
      <section className="py-8 md:py-10 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-xs uppercase tracking-[0.3em] text-slate-500 mb-2">
              {currentLang === 'de' ? 'Portfolio' : 'Portfolio'}
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
              {currentLang === 'de' ? 'Unsere jüngsten Referenzprojekte' : 'Our recent projects'}
            </h2>
            <p className="text-sm md:text-base text-slate-600">
              {currentLang === 'de'
                ? 'Wir zeigen Beispiele, wie strukturierte Planung Vermögen schützt und Wachstum möglich macht.'
                : 'A curated selection of mandates where structured planning protected wealth and unlocked growth.'}
            </p>
            <div className="flex items-center justify-center gap-3 mt-4">
              <button
                type="button"
                className="w-10 h-10 border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-100 transition"
                aria-label={currentLang === 'de' ? 'Zurück' : 'Previous'}
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                className="w-10 h-10 border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-100 transition"
                aria-label={currentLang === 'de' ? 'Weiter' : 'Next'}
              >
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="mt-8 overflow-hidden">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {carouselItems.map((item, index) => {
                const projectUrl = (item as any).url;
                const isInternal = (item as any).isInternal;
                const content = (
                  <>
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        loading={index > 2 ? 'lazy' : 'eager'}
                      />
                    </div>
                    <div className="p-3">
                      <h3 className="text-xs font-semibold text-slate-900 leading-snug">{item.title}</h3>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mt-1">{item.status}</p>
                      <p className="text-[11px] text-slate-600 mt-1">{item.result}</p>
                    </div>
                  </>
                );

                if (projectUrl && projectUrl !== '#') {
                  // Internal link - use React Router Link
                  if (isInternal) {
                    return (
                      <Link
                        key={`${item.image}-${index}`}
                        to={projectUrl}
                        className="group bg-white shadow-sm border border-slate-100 overflow-hidden hover:shadow-md transition-shadow duration-300 block"
                      >
                        {content}
                      </Link>
                    );
                  }
                  // External link - use anchor tag
                  return (
                    <a
                      key={`${item.image}-${index}`}
                      href={projectUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group bg-white shadow-sm border border-slate-100 overflow-hidden hover:shadow-md transition-shadow duration-300 block"
                    >
                      {content}
                    </a>
                  );
                }

                return (
                  <article
                    key={`${item.image}-${index}`}
                    className="group bg-white shadow-sm border border-slate-100 overflow-hidden hover:shadow-md transition-shadow duration-300"
                  >
                    {content}
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* How We Work Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16">
            <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-slate-500 mb-4">
              {currentLang === 'de' ? 'Unser Ansatz' : 'What we provide'}
            </p>
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900">
              {currentLang === 'de' ? 'Wie wir arbeiten' : 'How We Work'}
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Image with small badge */}
            <div className="relative">
              <img
                src="/assets/ChristianPlushy/chris-golden-blue-suit-piano-refined.webp"
                alt={currentLang === 'de' ? 'Fokussierte Beratung' : 'Focused Advisory Work'}
                className="w-full h-auto object-contain"
              />
              {/* Small badge overlay */}
              <div className="absolute bottom-4 left-4 bg-white/10 backdrop-blur-md px-3 py-1.5 shadow-lg border border-white/20">
                <p className="text-xs font-semibold text-white">
                  {currentLang === 'de' ? 'Klarheit & Freude' : 'Clarity & Joy'}
                </p>
              </div>
            </div>

            {/* Right: Process steps */}
            <div className="space-y-10">
              {/* Step 1 */}
              <div className="flex gap-6">
                <div className="flex-shrink-0 w-14 h-14 bg-slate-100 flex items-center justify-center">
                  <svg className="w-7 h-7 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">
                    {currentLang === 'de' ? 'Erstgespräch' : 'Preliminary Discussion'}
                  </h3>
                  <p className="text-lg text-slate-600 leading-relaxed">
                    {currentLang === 'de'
                      ? 'Wir beginnen mit einem einführenden Gespräch, um Ihre Ziele und Herausforderungen zu verstehen.'
                      : 'We start by arranging an introductory discussion to grasp your company\'s objectives and obstacles.'}
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-6">
                <div className="flex-shrink-0 w-14 h-14 bg-slate-100 flex items-center justify-center">
                  <svg className="w-7 h-7 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">
                    {currentLang === 'de' ? 'Analyse & Recherche' : 'Exploration and Investigation'}
                  </h3>
                  <p className="text-lg text-slate-600 leading-relaxed">
                    {currentLang === 'de'
                      ? 'Unser Team führt gründliche Recherchen durch, um wesentliche Erkenntnisse über Ihre Situation zu sammeln.'
                      : 'Our team engages in thorough research to collect essential insights regarding your sector, target audience, rivals, and ongoing strategies.'}
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-6">
                <div className="flex-shrink-0 w-14 h-14 bg-slate-100 flex items-center justify-center">
                  <svg className="w-7 h-7 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">
                    {currentLang === 'de' ? 'Strategieentwicklung' : 'Formulation of a Strategy'}
                  </h3>
                  <p className="text-lg text-slate-600 leading-relaxed">
                    {currentLang === 'de'
                      ? 'Aus den gesammelten Erkenntnissen entwickeln wir eine maßgeschneiderte Strategie, die auf Ihre Geschäftsziele abgestimmt ist.'
                      : 'Drawing from the gathered insights, we craft a strategic plan that resonates with your business objectives.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Latest News / Blog Section */}
      <section className="py-12 md:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-10 md:mb-16 gap-6 md:gap-8">
            <div>
              <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-slate-500 mb-4">
                {currentLang === 'de' ? 'Aktuelles' : 'News'}
              </p>
              <h2 className="text-3xl md:text-5xl font-bold text-slate-900 leading-tight">
                {currentLang === 'de' ? 'Unsere neuesten Beiträge' : 'Our latest news'}
              </h2>
            </div>
            <Button
              variant="primary"
              size="lg"
              className="w-full md:w-auto font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
              onClick={() => navigate(`/${currentLang}/blog`)}
            >
              {currentLang === 'de' ? 'Alle Beiträge ansehen' : 'View All'}
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Blog Post 1 */}
            <article className="group bg-white overflow-hidden shadow-md hover:shadow-xl transition-all duration-300">
              <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                <img
                  src="/assets/Example Images/style human/human-conductor-1.webp"
                  alt="Blog Post"
                  className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-3">
                  {currentLang === 'de'
                    ? 'Von der Gründung zu bemerkenswertem Erfolg'
                    : 'From Startup Roots to Remarkable Success'}
                </h3>
                <p className="text-slate-600 leading-relaxed mb-6">
                  {currentLang === 'de'
                    ? 'Der Weg vom Start-up zum Erfolg ist nicht immer glatt und bringt oft Herausforderungen mit sich. Erfahrene Beratung kann den entscheidenden Unterschied machen.'
                    : 'The road from startup to success is not always smooth, and it often comes with its challenges. Seasoned consultant can make all the difference.'}
                </p>
                <button
                  className="text-slate-900 font-medium inline-flex items-center group-hover:text-accent-copper transition-colors"
                  onClick={() => navigate(`/${currentLang}/blog`)}
                >
                  {currentLang === 'de' ? 'Mehr erfahren' : 'Read more'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              </div>
            </article>

            {/* Blog Post 2 */}
            <article className="group bg-white overflow-hidden shadow-md hover:shadow-xl transition-all duration-300">
              <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                <img
                  src="/assets/Example Images/style human/human-visualizing-connection-1.webp"
                  alt="Blog Post"
                  className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-3">
                  {currentLang === 'de'
                    ? 'Ein Leitfaden zur Entwicklung von Führungsqualitäten'
                    : 'A Guide To Leadership Development'}
                </h3>
                <p className="text-slate-600 leading-relaxed mb-6">
                  {currentLang === 'de'
                    ? 'Führung ist nicht nur ein Titel, sondern eine Fähigkeit, die im Laufe der Zeit entwickelt werden kann. Effektive Führung ist entscheidend für den Erfolg von Organisationen.'
                    : 'Leadership is not just a title, it\'s a skill that can be developed over time. Effective leadership is crucial for guiding organizations toward success.'}
                </p>
                <button
                  className="text-slate-900 font-medium inline-flex items-center group-hover:text-accent-copper transition-colors"
                  onClick={() => navigate(`/${currentLang}/blog`)}
                >
                  {currentLang === 'de' ? 'Mehr erfahren' : 'Read more'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Related Content Section */}
      <RelatedContent />
    </>
  );
};

export default HomePage;
